import { Modal } from '@snapi/layout';
import {
  Baby,
  BabyDiet,
  BacteriaReport,
  BacteriaResearchSummary,
  MicrobiomeTest,
  Parent,
  ReportSummary,
  ResearchCategory,
  ResearchCategoryStatsMap,
  TaxonomyDiversityStats,
  CheckOneParentOnPayPlan_private_parent_shopify,
} from '@snapi/types';
import Link from 'next/link';
import { Button } from '@snapi/luxury';
import {
  DateUtils,
  MicrobiomeTestResultUtils,
  ReportUtils,
} from '@snapi/utils';
import { AnimatePresence } from 'framer-motion';
import { differenceInCalendarMonths, format } from 'date-fns';
import { useMemo, useState } from 'react';
import { BacteriaResearchSummaryCard } from './bacteria-research-summary-card';
import { FeedbackForm } from './feedback-form';
import { ReportBacteriaList } from './report-bacteria-list';
import { ReportContext } from './report-context';
import { ReportFeedbackSection } from './report-feedback-section';
import { ReportHeader } from './report-header';
import { ReportRecommendationSummary } from './report-recommendation-summary';
import { ReportCallToAction } from './report-cta';
import { ReportRelatedConcernBacteria } from './report-related-concern';
import { ReportHealthRecomendations } from './report-health-recomendations';
import { ReportProbiotic } from './report-probiotic';
import { ReportProbioticPdf } from './report-probiotic';
import { useReportContext } from './report-context';
import html2pdf from 'html2pdf.js';
import ReactDOM from 'react-dom';
import { Subtitle, Description } from '@snapi/luxury';
import { PieChart } from 'react-minimal-pie-chart';
import { Percentage } from '@snapi/luxury';
import clsx from 'clsx';
import { FlagIcon } from '@snapi/icons';
import {
  ResearchCategoryToBackgroundColor,
  ResearchCategoryToColor,
} from '@snapi/ui-utils';
import { ReportRelatedConcernBacteriaPdf } from './report-related-concern';
import { ReportRecommendationSummaryPdf } from './report-recommendation-summary';
import { ReportHealthRecomendationsPdf } from './report-health-recomendations';
import { ReportBacteriaListPdf } from './report-bacteria-list';
import { DietitianAvatarIcon } from './report-pdf/images/dana-avatar';

interface ReportScreenProps {
  baby: Pick<Baby, 'name' | 'dob' | 'gender'>;
  parent: Pick<Parent, 'name' | 'email'>;
  test: Pick<
    MicrobiomeTest,
    | 'id'
    | 'diet'
    | ResearchCategory.protective
    | ResearchCategory.disruptive
    | ResearchCategory.neutral
    | ResearchCategory.unknown
    | 'summary'
    | 'createdAt'
    | 'recommendations'
  >;
  diet: BabyDiet[];
  summary: ReportSummary;
  bacteria: BacteriaReport[];
  condition: string[];
  categoryStats: ResearchCategoryStatsMap;
  diversity: TaxonomyDiversityStats;
  payPlan?: CheckOneParentOnPayPlan_private_parent_shopify | null | undefined;
}

export const ReportScreen = (props: ReportScreenProps) => {
  const [selected, setSelected] = useState<BacteriaResearchSummary | null>(
    null
  );

  type BacteriaNameMapProps = {
    [name: string]: {
      name: string;
      categories: string[];
      condition: string[];
    };
  };

  const bacteriaNameMap: BacteriaNameMapProps = {};

  // Create a Set to store unique conditions from props.condition
  const uniqueConditions = new Set(props.condition);

  // Iterate through the summary array and collect unique categories and conditions
  props.summary.keyBacteria.bacteria.forEach((item) => {
    const { name, category, condition } = item;

    // Check if the name already exists in the bacteriaNameMap, if not, initialize it with empty arrays
    if (!bacteriaNameMap[name]) {
      bacteriaNameMap[name] = {
        name: name,
        categories: [],
        condition: [],
      };
    }

    // Push the category and condition values into their respective arrays if they are unique
    if (category && !bacteriaNameMap[name].categories.includes(category)) {
      bacteriaNameMap[name].categories.push(category);
    }
    if (condition && !bacteriaNameMap[name].condition.includes(condition)) {
      bacteriaNameMap[name].condition.push(condition);
    }

    // Filter the conditions based on uniqueConditions
    bacteriaNameMap[name].condition = bacteriaNameMap[name].condition.filter((c) =>
      uniqueConditions.has(c)
    );
  });

  // Convert the bacteriaNameMap object into an array of objects
  const bacteriasWithCategoriesAndConditions = Object.values(bacteriaNameMap);

  const numberOfGood = useMemo(
    () =>
      (ReportUtils.diversity.isGood(props.summary.diversity.score) ? 1 : 0) +
      (ReportUtils.balance.isGood(
        ReportUtils.balance.calculateScore(
          props.summary.balance.protective,
          props.summary.balance.disruptive
        )
      )
        ? 1
        : 0) +
      (ReportUtils.disruptive.isGood(props.summary.disruptive.bacteria)
        ? 1
        : 0) +
      (ReportUtils.keyBacteria.isGood(props.summary.keyBacteria.bacteria)
        ? 1
        : 0),
    [
      props.summary.diversity,
      props.summary.disruptive,
      props.summary.keyBacteria,
      props.summary.balance,
    ]
  );
  const numberOfGoodAsText = (number: number) => {
    if (number < 2) {
      return 'could use some improvement';
    } else if (number < 4) {
      return 'development is on track';
    }
    return 'health is impressive';
  };

  type CardBadgeProps = {
    good: boolean;
    children: React.ReactNode;
  };

  const CardBadge: React.FC<CardBadgeProps> = ({ good, children }) => (
    <small
      className={clsx(
        'flex px-4 pb-4 rounded-full items-center justify-center',
        good ? 'bg-green' : 'bg-red text-white'
      )}
    >
      {children}
    </small>
  );

  type NewDashboardCardProps = {
    title: string;
    badge: { good: boolean; status: string };
    summary: string;
    children: React.ReactNode;
  };

  const NewDashboardCardPdf: React.FC<NewDashboardCardProps> = ({
    badge,
    title,
    summary,
    children,
  }) => {
    return (
      <div className="flex flex-col">
        <div className="flex shrink-0 flex-col rounded-[10px] w-[336px] h-[340px] p-3 relative bg-yellow-light text-body">
          <div className="flex pb-3 relative justify-center">
            <div className="flex flex-1 pb-3">
              <h5 className="uppercase">{title}</h5>
            </div>
            <CardBadge good={badge.good}>{badge.status}</CardBadge>
            <div className="absolute bottom-0 -left-3 -right-3 border-b border-gray-soft"></div>
          </div>
          <div className="flex justify-between">
            <div className="flex flex-col justify-between">
              <div className="flex flex-col">{children}</div>
              <div className="flex mt-5">
                <Subtitle>{summary}</Subtitle>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  interface CategorySummary {
    category: ResearchCategory;
    color: string;
    value: number;
  }

  interface CategorySummaryPieProps {
    data: CategorySummary[];
  }
  const CategorySummaryPie: React.FC<CategorySummaryPieProps> = ({
    data,
  }: CategorySummaryPieProps) => {
    const dominantCategory = useMemo(
      () => data.sort((a, b) => b.value - a.value)[0],
      [data]
    );

    return (
      <div className="flex relative max-w-[256px]">
        <div className="absolute bottom-5 lg:bottom-2 xl:bottom-4 right-0 left-0 flex items-end justify-center">
          <div className="text-center">
            <h4 className="uppercase text-[16px]">
              {dominantCategory.value.toFixed(1)} %
              <br />
              {dominantCategory.category}
            </h4>
          </div>
        </div>
        <PieChart
          data={data}
          startAngle={190}
          lengthAngle={160}
          viewBoxSize={[100, 45]}
          lineWidth={10}
          rounded
          paddingAngle={8}
          totalValue={100}
        />
        <div className="absolute top-0 left-0 right-0 bottom-0 mx-[22px] mt-2">
          <PieChart
            data={new Array(25)
              .fill(null)
              .map(() => ({ value: 4, color: 'rgba(0, 0, 0, .3)' }))}
            startAngle={180}
            lengthAngle={180}
            viewBoxSize={[100, 40]}
            lineWidth={0.5}
            paddingAngle={2}
            totalValue={100}
          />
        </div>
      </div>
    );
  };
  const GutBalanceCardPdf: React.FC<
    Pick<ReportSummary['balance'], 'protective' | 'disruptive' | 'neutral'>
  > = ({ protective, disruptive, neutral }) => {
    const summary = useMemo<CategorySummary[]>(
      () => [
        {
          category: ResearchCategory.protective,
          color: ResearchCategoryToColor[ResearchCategory.protective],
          value: protective,
        },
        {
          category: ResearchCategory.disruptive,
          color: ResearchCategoryToColor[ResearchCategory.disruptive],
          value: disruptive,
        },
        {
          category: ResearchCategory.neutral,
          color: ResearchCategoryToColor[ResearchCategory.neutral],
          value: neutral,
        },
        {
          category: ResearchCategory.other,
          color: ResearchCategoryToColor[ResearchCategory.other],
          value: 100 - protective - disruptive - neutral,
        },
      ],
      [protective, disruptive, neutral]
    );

    const { good, score } = useMemo(() => {
      const score = ReportUtils.balance.calculateScore(protective, disruptive);
      const good = ReportUtils.balance.isGood(score);
      return { good, score };
    }, [protective, disruptive]);

    return (
      <NewDashboardCardPdf
        badge={{ good, status: good ? 'Nice work' : 'Attention Needed' }}
        title="Gut Balance"
        summary={`${props.baby.name}’s gut balance is dominated by ${
          good
            ? 'protective bacteria. Nice work!'
            : 'disruptive bacteria. Let’s work on it!'
        }`}
      >
        <div className="flex flex-1 flex-col justify-center mt-6">
          <div className="flex justify-center">
            <CategorySummaryPie data={summary} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            {summary.map(({ category, value }) => (
              <div className="flex ">
                <span
                  className={clsx(
                    'items-center justify-center w-3 h-3 rounded-full mt-2',
                    ResearchCategoryToBackgroundColor[category]
                  )}
                />
                <small className="pl-2 ">
                  <Percentage percentage={value} /> - {category}
                </small>
              </div>
            ))}
          </div>
        </div>
      </NewDashboardCardPdf>
    );
  };
  const scoreToText = (score: number, baby: string) => {
    if (score < 15) {
      return `${props.baby.name}'s diversity could use some work.`;
    } else if (score < 85) {
      return `${props.baby.name}'s gut shows healthy diversity. Looking good!`;
    } else {
      return `${props.baby.name}’s gut is diverse.`;
    }
  };
  const DiversityCardPdf: React.FC<
    Omit<ReportSummary['diversity'], 'good'>
  > = ({ score }) => {
    const good = ReportUtils.diversity.isGood(score);

    return (
      <NewDashboardCardPdf
        badge={{ good, status: good ? 'Nice work' : 'Attention Needed' }}
        title="Diversity"
        summary={scoreToText(score, props.baby.name)}
      >
        <div className="flex flex-col flex-1 justify-center mt-16 mb-20">
          <div className="flex pt-7 relative max-h-20 ">
            <div className="absolute top-0 left-0 bottom-0 right-0 flex z-50">
              <div
                style={{ width: `${score}%` }}
                className="text-left flex justify-end"
              />
              <FlagIcon className="top-1 w-[28px] h-[48px] " />
            </div>
            <div
              className="h-5 rounded-md flex flex-1 relative w-[312px]"
              style={{
                background:
                  'linear-gradient(90deg, #FFE69A 0%, #D3EDC8 72.56%)',
              }}
            />
            <div className="h-7 w-[1px] absolute bg-body top-[24px] left-[46px] "></div>
            <div className="h-7 w-[1px] absolute bg-body top-[24px] left-[256px] "></div>
          </div>
          <div className="flex mt-2 justify-between">
            <div className="flex">
              <Description>Low</Description>
            </div>
            <div className="flex">
              <Description>Normal</Description>
            </div>
            <div className="flex">
              <Description>High</Description>
            </div>
          </div>
        </div>
      </NewDashboardCardPdf>
    );
  };
  const generatePDF = async () => {
    try {
      const module = await import('html2pdf.js');
      const html2pdf = module.default;
      const renderComponent = (babyName: string) => {
        return (
          <div className="bg-purple">
            <div className="container py-10 bg-purple text-purple-dark px-10">
              <h5 className="mb-3">
                sample from
                <span className="ml-[2px]">
                  {format(
                    new Date(props.test.createdAt || Date.now()),
                    'MMMM dd, yyyy'
                  )}
                </span>
              </h5>
              <h1 className="mb-[40px]">
                <span className="text-white">{babyName}'s</span>{' '}
                {numberOfGoodAsText(numberOfGood)}
              </h1>
              <div className="flex flex-wrap items-start">
                <div className="flex items-center gap-4 mb-2 ">
                  <div className="w-[60px] shrink-0">
                    {' '}
                    <DietitianAvatarIcon />
                  </div>

                  <div className="flex flex-col">
                    <h5 className="mb-2">Personal Dietitian</h5>
                    <p className="font-semibold">
                      Hi! I'm Dana, and I'm here to help you better understand
                      what's happing in {babyName}'s gut. Let's get started!
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-row gap-10 items-center justify-center mt-16">
                <DiversityCardPdf {...props.summary.diversity} />
                <GutBalanceCardPdf {...props.summary.balance} />
              </div>
            </div>

            <div className="bg-yellow-light min-h-screen px-10">
              <ReportRelatedConcernBacteriaPdf
                bacteria={keyBacteriaSummary}
                attention={ReportUtils.attention.pick(summaryTransformed)}
                condition={props.condition}
                babyName={props.baby.name}
                babyGender={props.baby.gender}
              />
              <ReportRecommendationSummaryPdf
                recommendations={recomenditionsWithNeededCategories}
                babyName={props.baby.name}
              />
              <ReportProbioticPdf
                concerns={props.condition}
                ageTested={babyAgeInMomentTest}
              />
              <ReportHealthRecomendationsPdf babyName={props.baby.name} />
            </div>
            <div className="px-10 bg-blue-light">
              <ReportBacteriaListPdf
                summary={bacteriaResearchSummary}
                other={
                  100 -
                  props.test.disruptive -
                  props.test.protective -
                  props.test.neutral -
                  props.test.unknown
                }
              />
            </div>
          </div>
        );
      };

      const element = document.createElement('div');
      ReactDOM.render(renderComponent(props.baby.name), element);

      const options = {
        filename: 'report.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: {
          unit: 'in',
          format: [8.5, 150],
          orientation: 'portrait',
          compress: true,
        },
      };

      // Generate the PDF
      html2pdf().from(element).set(options).save();
    } catch (error) {
      console.error('Error importing html2pdf.js:', error);
    }
  };

  const babyAgeInMomentTest = differenceInCalendarMonths(
    new Date(props.test.createdAt),
    new Date(props.baby.dob)
  );

  const recFood = props.test.recommendations.filter((item) => {
    return (
      item.recommendation.category === 'mother' ||
      item.recommendation.category === 'food'
    );
  });
  recFood.sort((a, b) => {
    if (
      a.recommendation.category === 'mother' &&
      b.recommendation.category !== 'mother'
    ) {
      return -1;
    } else if (
      a.recommendation.category !== 'mother' &&
      b.recommendation.category === 'mother'
    ) {
      return 1;
    } else {
      return 0;
    }
  });

  const bacteriaResearchSummary = useMemo(
    () => MicrobiomeTestResultUtils.groupByAncestor(props.test.summary || []),
    [props.test.summary]
  );

  const summaryWithDescription = props.summary.keyBacteria.bacteria.map(
    (bacteria) => {
      const description = bacteriaResearchSummary.find(
        (item) => item.bacteria.research.name === bacteria.name
      );
      const descriptionFull = description?.bacteria.research.description;
      return {
        ...bacteria,
        description: descriptionFull,
      };
    }
  );

  const keyBacteriaSummary = {
    bacteria: summaryWithDescription,
    good: props.summary.keyBacteria.good,
  };
  const { keyBacteria, ...rest } = props.summary;
  const summaryTransformed = { keyBacteria: keyBacteriaSummary, ...rest };

  const handleSelect = (bacteria: string) => {
    if (selected?.bacteria.research.name === bacteria) {
      return setSelected(null);
    }

    const summary = MicrobiomeTestResultUtils.extractSummary(
      bacteria,
      props.bacteria,
      props.test.summary
    );
    setSelected(summary || null);
  };

  const closeModal = () => setSelected(null);

  const recomenditionsWithNeededCategories = recFood.map((recFoodItem) => {
    // Check if 'concern' is "diversity" or "balance"
    if (
      recFoodItem.reason.concern  === 'diversity' ||
      recFoodItem.reason.concern === 'balance'
    ) {
      return recFoodItem; // Keep unchanged
    }

    const matchingResultItem = bacteriasWithCategoriesAndConditions.find(
      (resultItem) => resultItem.name === recFoodItem.reason.name
    );


    return {
      ...recFoodItem,
      categories: matchingResultItem?.categories,
      condition: matchingResultItem?.condition,
    };
  });


  return (
    <ReportContext.Provider
      value={{
        test: { id: props.test.id, diet: props.test.diet },
        baby: {
          name: props.baby.name,
          ageInMonths: DateUtils.ageInMonths(props.baby.dob),
          gender: props.baby.gender,
        },
        parent: props.parent,
      }}
    >
      <div className="bg-purple">
        <div className="px-5 sm:px-8 xl:px-0 max-w-[1200px] mx-auto flex justify-between items-center h-[80px] bg-purple border-b-[1px] border-white">
          <div className="w-[120px]">
            <Link href={'https://my.getsnapi.com/dashboard'}>
              <Button size="xs" secondary>
                Back
              </Button>
            </Link>
          </div>
          <h5 className="hidden sm:flex text-purple-dark">
            {`Sample from ${format(
              new Date(props.test.createdAt || Date.now()),
              'MMMM dd, yyyy'
            )} `}
          </h5>
          <div className="w-[120px]">
            <Button size="xs" onClick={generatePDF}>
              Download
            </Button>{' '}
          </div>
        </div>
      </div>
      <div id="report-screen">
        <ReportHeader
          summary={props.summary}
          reportedAt={props.test.createdAt}
          onSelect={handleSelect}
        />
        <div className="bg-yellow-light min-h-screen">
          <ReportRelatedConcernBacteria
            bacteria={props.summary.keyBacteria}
            attention={ReportUtils.attention.pick(props.summary)}
            onSelect={handleSelect}
            condition={props.condition}
          />

          <ReportRecommendationSummary recommendations={recomenditionsWithNeededCategories} />
          <ReportProbiotic
            concerns={props.condition}
            ageTested={babyAgeInMomentTest}
          />

          <ReportHealthRecomendations />
          <ReportBacteriaList
            summary={bacteriaResearchSummary}
            other={
              100 -
              props.test.disruptive -
              props.test.protective -
              props.test.neutral -
              props.test.unknown
            }
          />
          <FeedbackForm />
          {props.payPlan?.productSubscriberStatus !== 'ACTIVE' && (
            <ReportCallToAction />
          )}

          <ReportFeedbackSection />
        </div>
        <AnimatePresence initial={false} exitBeforeEnter>
          {selected !== null && (
            <Modal onClose={closeModal}>
              <div className="px-3">
                <BacteriaResearchSummaryCard
                  {...selected}
                  expanded
                  filter={{ categories: [], conditions: [] }}
                  onExpand={closeModal}
                />
              </div>
            </Modal>
          )}
        </AnimatePresence>
      </div>
    </ReportContext.Provider>
  );
};
